import * as React from 'react'
// libs
import styled from 'styled-components'
import { useSnackbar } from 'notistack'
import { useMutation } from '@apollo/client/react'
// components
import MenuItemOrigin from '@mui/material/MenuItem'
// constants
import { Modals } from 'constants/index'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { ObjectivesListTreeBLContext } from 'containers/ObjectivesList/layers/BL/Tree'
// graphql
import { PLAN_OBJECTIVE_DELETE_MUTATION } from 'graphql/planObjectives/mutations'
import { PLAN_KPI_DELETE_MUTATION } from 'graphql/planKPIs/mutations'
import { PLAN_ACTION_DELETE_MUTATION } from 'graphql/planActions/mutations'
import { PLAN_ITEM_SET_ARCHIVED_MUTATION } from 'graphql/plan/mutations'
// hooks
import { useShowModal, usePermissions } from 'hooks/index'
// themes
import { colors } from 'themes'
// types
import { IPlanTypeLevelOption } from 'types/graphql'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { collectNodeChilds } from 'utils/tree'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

// styled
const Wrapper = styled('div')`
  padding: 8px 0;
`

const MenuItem = styled(MenuItemOrigin)`
  && {
    color: ${colors.blue500};
  }
`

// types
interface IIncomingProps {
  node: any
  tree: any
  isKPI: boolean
  isAction: boolean
  isObjective: boolean
  closePopup: (event?: React.MouseEvent) => void
  handleActionAdd: (event: React.MouseEvent) => void
  handleKPIAdd: (event: React.MouseEvent) => void
  handleObjectiveAdd: (event: React.MouseEvent, typeOption?: IPlanTypeLevelOption) => void
  nextLevelDescription: string
  shouldShowAddKPIButton: boolean
  shouldShowAddActionButton: boolean
  shouldShowAddObjectiveButton: boolean
}

const PlanItemActionsMenu: React.FC<IIncomingProps> = ({
  node,
  tree,
  isKPI,
  isAction,
  isObjective,
  closePopup,
  handleKPIAdd,
  handleActionAdd,
  handleObjectiveAdd,
  nextLevelDescription,
  shouldShowAddKPIButton,
  shouldShowAddActionButton,
  shouldShowAddObjectiveButton
}) => {
  const [deleteKPIMutation] = useMutation(PLAN_KPI_DELETE_MUTATION)
  const [deleteActionMutation] = useMutation(PLAN_ACTION_DELETE_MUTATION)
  const [deleteObjectiveMutation] = useMutation(PLAN_OBJECTIVE_DELETE_MUTATION)

  const [archiveMutation] = useMutation(PLAN_ITEM_SET_ARCHIVED_MUTATION)

  const { showArchivedPlanItems } = React.useContext(PlanEditPageContext)

  // context
  const { enableLinkingMode } = React.useContext(LinkingModeContext)
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)
  const { debouncedSynchronizeTree } = React.useContext(ObjectivesListTreeBLContext)

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const {
    actionDeleteAction,
    kpiDeleteKPI,
    objectiveDeleteObjective,
    objectiveArchiveObjective,
    actionArchiveAction,
    kpiArchiveKPI
  } = usePermissions({
    entity: node.entity,
    isAction,
    isKPI,
    isObjective
  })

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_PLAN_ITEM })

  const canArchive =
    (isObjective && objectiveArchiveObjective) || (isAction && actionArchiveAction) || (isKPI && kpiArchiveKPI)

  const shouldArchive = canArchive && !node.archived

  const canDelete =
    (isObjective && objectiveDeleteObjective) || (isAction && actionDeleteAction) || (isKPI && kpiDeleteKPI)

  const levelOptions: IPlanTypeLevelOption[] = node.nextLevel?.typeOptions || []

  // methods
  const addObjective = (event, typeOption?) => {
    handleObjectiveAdd(event, typeOption)
    closePopup(event)
  }

  const addAction = (event) => {
    handleActionAdd(event)
    closePopup(event)
  }

  const addKPI = (event) => {
    handleKPIAdd(event)
    closePopup(event)
  }

  const linkPlanItem = (event) => {
    enableLinkingMode(node.entityId, node.planId, node.title, node.entityType)
    closePopup(event)
  }

  const archivePlanItem = async (event) => {
    closePopup(event)

    showModal({
      node,
      handleConfirm: async () => {
        try {
          await archiveMutation({ variables: { id: node.id, archived: true } })
          await treeQueryRefetch()
        } catch (errors) {
          handleError(errors, 'PlanItemActionsMenu.archivePlanItem')
        }
      }
    })
  }

  const deletePlanItem = (event) => {
    closePopup(event)

    if (node.referenced || get(node, 'entity.planItems.data', []).length > 1) {
      return enqueueSnackbar('Record can not be deleted as it is referenced by another record!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }

    showModal({
      node,
      handleConfirm: async () => {
        try {
          let deleteMutation

          if (isObjective) {
            deleteMutation = deleteObjectiveMutation
          }

          if (isAction) {
            deleteMutation = deleteActionMutation
          }

          if (isKPI) {
            deleteMutation = deleteKPIMutation
          }

          await deleteMutation({ variables: { id: node.entityId } })

          await treeQueryRefetch()

          const deletedNodes = collectNodeChilds(node)
          const treeFilter = ({ id }) => !deletedNodes.includes(id)

          debouncedSynchronizeTree(tree, treeFilter)
        } catch (errors) {
          handleError(errors, 'PlanItemActionsMenu.deletePlanItem')
        }
      }
    })
  }

  const unarchivePlanItem = async (event) => {
    closePopup(event)

    try {
      await archiveMutation({ variables: { id: node.id, archived: false } })
      await treeQueryRefetch()
    } catch (errors) {
      handleError(errors, 'PlanItemActionsMenu.unarchivePlanItem')
    }
  }

  const handleRemoveClick = (event) => (shouldArchive ? archivePlanItem(event) : deletePlanItem(event))

  return (
    <Wrapper>
      {!node.archived && (
        <>
          {shouldShowAddObjectiveButton && (
            <>
              <MenuItem onClick={(event) => addObjective(event)}>Add {nextLevelDescription}</MenuItem>
              {levelOptions.map((typeOption) => (
                <MenuItem key={typeOption.id} onClick={(event) => addObjective(event, typeOption)}>
                  Add {typeOption.label}
                </MenuItem>
              ))}
            </>
          )}
          {shouldShowAddActionButton && <MenuItem onClick={addAction}>Add Action</MenuItem>}
          {shouldShowAddKPIButton && <MenuItem onClick={addKPI}>Add Indicator</MenuItem>}
          <MenuItem onClick={linkPlanItem}>Link Item</MenuItem>
        </>
      )}
      {canArchive && showArchivedPlanItems && node.archived && (
        <MenuItem onClick={unarchivePlanItem}>Unarchive</MenuItem>
      )}
      {(canArchive || canDelete) && (
        <MenuItem onClick={handleRemoveClick}>{shouldArchive ? 'Archive' : 'Delete'}</MenuItem>
      )}
    </Wrapper>
  )
}

export default PlanItemActionsMenu
